/**
 * TreeDetailsPanel - Panel de detalles completo del árbol con pestañas
 *
 * @version 1.2
 * @updated 2026-02-18
 * @module core/map-tools
 *
 * @description
 * Panel con 5 pestañas para gestión completa de arbolado público:
 * 1. General - Identificación y ubicación
 * 2. Estado - Mediciones y salud
 * 3. Riesgo - Evaluación de riesgo y entorno
 * 4. Mantenimiento - Intervenciones y historial
 * 5. Patrimonio - Valor ecológico y datos administrativos
 *
 * @remarks
 * Implementa sistema de detección de cambios sin guardar para prevenir pérdida de datos.
 * Integra el sistema de codificación automática de objetos (core/coding):
 * - Árboles nuevos: genera código TR_XXNNN_NNN al guardar
 * - Árboles movidos a otra celda: registra cambio en historial y muestra aviso
 */

import { useState, useEffect, useMemo } from 'react';
import type { Tree } from '../tree-types';
import { GeneralTab } from './tabs/GeneralTab';
import { HealthTab } from './tabs/HealthTab';
import { RiskTab } from './tabs/RiskTab';
import { MaintenanceTab } from './tabs/MaintenanceTab';
import { HeritageTab } from './tabs/HeritageTab';
import { useRightPanel, useUnsavedChanges } from '../../layouts/panels/RightPanel';
import { useEditLock } from '../../layouts';
import { ConfirmDialog } from '../../components';
import { useObjectCode, ObjectCodeBadge, CodeChangeWarning } from '../../coding';
import { PointAssociationSection } from '../../spatial';
import './tree-details-panel.css';

/**
 * Tipo de pestañas disponibles
 */
type TabId = 'general' | 'health' | 'risk' | 'maintenance' | 'heritage';

/**
 * Props del componente
 */
export interface TreeDetailsPanelProps {
  /**
   * Datos del árbol a mostrar/editar
   */
  tree: Tree;

  /**
   * ID del proyecto activo (necesario para la codificación automática)
   */
  projectId: string | null;

  /**
   * Callback cuando se guarda el árbol
   */
  onSave: (updatedTree: Tree) => void;

  /**
   * Callback cuando se cancela la edición
   */
  onCancel: () => void;

  /**
   * Callback cuando se elimina el árbol
   */
  onDelete: (treeId: string) => void;

  /**
   * Si true, el panel es de solo lectura: campos deshabilitados,
   * sin botones de guardado ni eliminación.
   * Se usa en modos distintos al modo Edición.
   */
  readOnly?: boolean;
}

/**
 * Componente TreeDetailsPanel
 */
export function TreeDetailsPanel({
  tree,
  projectId,
  onSave,
  onCancel,
  onDelete,
  readOnly = false,
}: TreeDetailsPanelProps): JSX.Element {
  const { registerUnsavedChangesCheck } = useRightPanel();
  const { startEditing, stopEditing, markUnsavedChanges, isEditingActive } = useEditLock();
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [formData, setFormData] = useState<Tree>(tree);
  const [originalData, setOriginalData] = useState<Tree>(tree); // Estado separado para el original
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Detecta si es un árbol nuevo (no persistido en BD)
   * Los árboles nuevos tienen IDs temporales que empiezan con 'tree-'
   */
  const isNewTree = useMemo(() => {
    return tree.id.startsWith('tree-');
  }, [tree.id]);

  // Hook de codificación automática de objetos
  const { recodeStatus, isCheckingRecode, generateCode, confirmRecode } = useObjectCode({
    objectType: 'TR',
    projectId,
    isNewObject: isNewTree,
    currentCode: tree.code,
    objectId: isNewTree ? undefined : tree.id,
    coordinates: formData.coordinates,
    originalCoordinates: tree.coordinates,
  });

  // Hook para detectar cambios sin guardar
  const { hasUnsavedChanges: hasFormChanges, confirmDiscard } = useUnsavedChanges(
    originalData, // Estado original (sincronizado con formData)
    formData, // Estado actual
    {
      confirmMessage: isNewTree
        ? '¿Descartar árbol sin guardar?\n\nEl árbol desaparecerá del mapa y todos los datos se perderán.'
        : '¿Descartar cambios del árbol?\n\nLos cambios realizados se perderán, pero el árbol se mantendrá con sus datos originales.',
    }
  );

  // Árboles nuevos (no guardados en BD) SIEMPRE tienen cambios sin guardar
  const hasUnsavedChanges = isNewTree || hasFormChanges;

  /**
   * Resetea el formulario cuando cambia el árbol
   *
   * @remarks
   * Este useEffect es CRÍTICO para que el panel cambie automáticamente
   * cuando el usuario hace clic en otro árbol desde el sidebar.
   * Sin este efecto, el formulario mantiene los datos del árbol anterior.
   *
   * IMPORTANTE: Resetea tanto formData como originalData para evitar
   * falsos positivos de "cambios sin guardar" al cambiar de árbol.
   */
  useEffect(() => {
    console.log('[TreeDetailsPanel] ====================================');
    console.log('[TreeDetailsPanel] useEffect disparado - tree.id cambió');
    console.log('[TreeDetailsPanel]   Nuevo tree.id:', tree.id);
    console.log('[TreeDetailsPanel]   Código:', tree.code);
    console.log('[TreeDetailsPanel]   Nombre común:', tree.commonName);
    console.log('[TreeDetailsPanel] Reseteando formulario y pestaña activa...');
    setFormData(tree);
    setOriginalData(tree); // También resetear el original para evitar falsos positivos
    setActiveTab('general');

    // Desactivar bloqueo de edición al cambiar de árbol
    stopEditing();

    console.log('[TreeDetailsPanel] ✓ Formulario reseteado');
    console.log('[TreeDetailsPanel] ====================================');
  }, [tree.id, stopEditing]); // Se ejecuta cuando cambia el ID del árbol

  /**
   * Registrar función de verificación de cambios sin guardar
   */
  useEffect(() => {
    registerUnsavedChangesCheck(confirmDiscard);

    return () => {
      registerUnsavedChangesCheck(null);
    };
  }, [confirmDiscard, registerUnsavedChangesCheck]);

  /**
   * Actualiza los datos del formulario
   */
  const handleFieldChange = (field: keyof Tree, value: any): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Activar bloqueo de edición si no está ya activo y no es readOnly
    if (!readOnly && !isEditingActive) {
      startEditing('tree');
    }

    // Marcar que hay cambios no guardados
    if (!readOnly) {
      markUnsavedChanges();
    }
  };

  /**
   * Guarda los cambios, generando o actualizando el código si corresponde.
   *
   * @remarks
   * - Árbol NUEVO: llama a generateCode() para obtener código del backend
   * - Árbol EXISTENTE movido a otra celda: llama a confirmRecode() para
   *   registrar el cambio en el historial y obtener el nuevo código
   * - En caso de fallo de la API de codificación, guarda sin cambiar el código
   */
  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    try {
      let code = formData.code;

      if (isNewTree && projectId) {
        const generatedCode = await generateCode();
        if (generatedCode) code = generatedCode;
      } else if (!isNewTree && recodeStatus?.willRecode && projectId) {
        const newCode = await confirmRecode();
        if (newCode) code = newCode;
      }

      const updatedTree: Tree = {
        ...formData,
        code,
        lastUpdate: new Date(),
        updatedBy: 'Sistema', // TODO: Obtener usuario actual
      };

      onSave(updatedTree);
      // Marcar como guardado actualizando el original
      setOriginalData(updatedTree);

      // Desactivar bloqueo de edición
      stopEditing();
    } catch (error) {
      console.error('[TreeDetailsPanel] Error al procesar código:', error);
      // No bloquear el guardado si la API de codificación falla
      const updatedTree: Tree = {
        ...formData,
        lastUpdate: new Date(),
        updatedBy: 'Sistema',
      };
      onSave(updatedTree);
      // Marcar como guardado actualizando el original
      setOriginalData(updatedTree);

      // Desactivar bloqueo de edición
      stopEditing();
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Cancela la edición con verificación de cambios
   *
   * @remarks
   * **Árbol NUEVO**: Si confirma descartar, el árbol se eliminará del mapa (nunca fue persistido)
   * **Árbol EXISTENTE**: Si confirma descartar cambios, se mantiene el árbol con datos originales
   */
  const handleCancelOrDiscard = async (): Promise<void> => {
    if (hasUnsavedChanges && !await confirmDiscard()) {
      return; // Usuario canceló el descarte, mantener panel abierto
    }
    // Usuario aceptó descartar: cerrar panel
    setFormData(tree); // Resetear cambios

    // Desactivar bloqueo de edición
    stopEditing();

    onCancel();
  };

  /**
   * Elimina el árbol con confirmación (solo para árboles existentes)
   */
  const handleDelete = async (): Promise<void> => {
    // Primero verificar cambios sin guardar
    if (hasUnsavedChanges) {
      const discardConfirmed = await confirmDiscard();
      if (!discardConfirmed) {
        return; // Usuario canceló
      }
    }

    // Confirmar eliminación con ConfirmDialog
    const confirmed = await ConfirmDialog.show({
      title: '¿Eliminar árbol?',
      message: 'Esta acción no se puede deshacer.\n\nEl árbol será eliminado permanentemente de la base de datos.',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      type: 'danger',
    });

    if (confirmed) {
      onDelete(tree.id);
    }
  };

  return (
    <div className="tree-details-panel">
      {/* Encabezado con código del objeto */}
      <div className="tree-code-header">
        <span className="tree-code-header__label">Código</span>
        <ObjectCodeBadge
          code={formData.code}
          isChecking={isCheckingRecode}
          isPending={isNewTree}
        />
      </div>

      {/* Pestañas */}
      <div className="tree-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'general'}
          className={`tree-tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          General
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'health'}
          className={`tree-tab ${activeTab === 'health' ? 'active' : ''}`}
          onClick={() => setActiveTab('health')}
        >
          Estado
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'risk'}
          className={`tree-tab ${activeTab === 'risk' ? 'active' : ''}`}
          onClick={() => setActiveTab('risk')}
        >
          Riesgo
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'maintenance'}
          className={`tree-tab ${activeTab === 'maintenance' ? 'active' : ''}`}
          onClick={() => setActiveTab('maintenance')}
        >
          Mant.
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'heritage'}
          className={`tree-tab ${activeTab === 'heritage' ? 'active' : ''}`}
          onClick={() => setActiveTab('heritage')}
        >
          Patrim.
        </button>
      </div>

      {/* Contenido de la pestaña activa */}
      <div className="tree-tab-content">
        {/* Wrapper que deshabilita interacción en modo solo-lectura */}
        <div style={readOnly ? { pointerEvents: 'none', opacity: 0.65 } : undefined}>
          {activeTab === 'general' && (
            <GeneralTab tree={formData} onChange={handleFieldChange} />
          )}
          {activeTab === 'health' && (
            <HealthTab tree={formData} onChange={handleFieldChange} />
          )}
          {activeTab === 'risk' && (
            <RiskTab tree={formData} onChange={handleFieldChange} />
          )}
          {activeTab === 'maintenance' && (
            <MaintenanceTab tree={formData} onChange={handleFieldChange} />
          )}
          {activeTab === 'heritage' && (
            <HeritageTab tree={formData} onChange={handleFieldChange} />
          )}
        </div>
      </div>

      {/* Sección de asociación a red urbana (solo para árboles existentes) */}
      {projectId && !isNewTree && (
        <PointAssociationSection
          projectId={projectId}
          entityType="tree"
          entityId={tree.id}
          coordinates={formData.coordinates}
          compact={false}
        />
      )}

      {/* Aviso de cambio de código (solo cuando el árbol se movió a otra celda) */}
      {recodeStatus?.willRecode && (
        <CodeChangeWarning
          currentCode={recodeStatus.currentCode}
          tentativeNewCode={recodeStatus.tentativeNewCode}
        />
      )}

      {/* Botones de acción */}
      <div className="tree-actions">
        {readOnly ? (
          /* Solo lectura: único botón para cerrar */
          <button
            className="tree-btn tree-btn-secondary"
            onClick={onCancel}
          >
            Cerrar
          </button>
        ) : (
          /* Modo edición: guardar, cancelar, eliminar */
          <>
            <button
              className="tree-btn tree-btn-primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Guardando...' : 'Guardar'}
            </button>

            <button
              className={isNewTree ? 'tree-btn tree-btn-danger' : 'tree-btn tree-btn-secondary'}
              onClick={handleCancelOrDiscard}
              disabled={isSaving}
            >
              {isNewTree ? 'Descartar' : 'Cancelar'}
            </button>

            {!isNewTree && (
              <button
                className="tree-btn tree-btn-danger"
                onClick={handleDelete}
                disabled={isSaving}
              >
                Eliminar
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
